import React, { useEffect, useState } from "react";
import { Button, Col, Card, Container, Row, Table } from "react-bootstrap";
import {
  DatatableWrapper,
  Filter,
  Pagination,
  TableBody,
  TableHeader,
} from "react-bs-datatable";
import { FaEdit, FaTrash, FaPlus } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { ShimmerTable } from "../common/ShimmerTable";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import ChatBotflowApi from "../../api/ChatBotflowApi";
import Confirm from "../Confirm";
import jwt_decode from "jwt-decode";

const ChatBotFlowList = ({ selectedWhatsAppSetting }) => {
  const navigate = useNavigate();
  const [flows, setFlows] = useState([]);
  const [isLoading, setIsLoading] = useState(true); // controls spinner & loading state
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [selectedFlow, setSelectedFlow] = useState(null);
  const [error, setError] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [userInfo, setUserInfo] = useState(
    jwt_decode(sessionStorage.getItem("token")),
  );
  const [chatbotLimit, setChatbotLimit] = useState(null);
  useEffect(() => {
    if (selectedWhatsAppSetting) {
      fetchFlows();
    }
  }, [selectedWhatsAppSetting]);
  useEffect(() => {
    if (userInfo?.addons) {
      const chatbotAddon = userInfo.addons.find(
        (addon) => addon.name === "chatbot",
      );

      if (chatbotAddon) {
        setChatbotLimit(chatbotAddon.quantity);
      } else {
        setChatbotLimit(null); // null = unlimited
      }
    }
  }, [userInfo]);
  const fetchFlows = async () => {
    try {
      setIsLoading(true);
      const result = await ChatBotflowApi.getActiveChatbotFlows(
        selectedWhatsAppSetting,
      );
      if (result.success) {
        setFlows(result.data?.rows || []);
        setError(null);
      } else {
        setError(result.message || "Failed to fetch flows");
        setFlows([]);
      }
    } catch (error) {
      // console.error("Error fetching flows:", error);
      setError("Failed to fetch flows. Please try again.");
      setFlows([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreate = () => {
    navigate("/chatbot/add", { state: { selectedWhatsAppSetting } });
  };

  const handleEdit = (flow) => {
    navigate(`/chatbot/${flow.id}/edit`, {
      state: { flow, selectedWhatsAppSetting },
    });
  };

  const handleDelete = (row) => {
    setSelectedFlow(row);
    setShowConfirmModal(true);
  };

  const confirmDelete = async () => {
    if (!selectedFlow?.id) {
      toast.error("Invalid flow selected for deletion");
      setShowConfirmModal(false);
      setSelectedFlow(null);
      return;
    }

    setDeleting(true);

    try {
      const result = await ChatBotflowApi.deleteChatbotFlow(selectedFlow.id);
      if (result.success) {
        toast.success("Flow deleted successfully", { autoClose: 1000 });
        await fetchFlows(); // Refresh the list after successful deletion
      } else {
        toast.error(result.message || "Failed to delete flow", {
          autoClose: 1000,
        });
      }
    } catch (error) {
      // console.error("Error deleting flow:", error);
      const errorMessage =
        error.response?.data?.error ||
        error.message ||
        "Failed to delete flow. Please try again.";
      toast.error(errorMessage, { autoClose: 1000 });
    } finally {
      setDeleting(false);
      setShowConfirmModal(false);
      setSelectedFlow(null);
    }
  };

  const handleStatusChange = async (flow) => {
    try {
      const newStatus = flow.status === "active" ? "inactive" : "active";
      const result = await ChatBotflowApi.updateFlowStatus(flow.id, newStatus);
      if (result.success) {
        toast.success(
          `Flow ${
            newStatus === "active" ? "activated" : "deactivated"
          } successfully`,
          { autoClose: 1000 },
        );
        fetchFlows();
      } else {
        toast.error(result.message || "Failed to update flow status", {
          autoClose: 1000,
        });
      }
    } catch (error) {
      // console.error("Error updating flow status:", error);
      toast.error("Failed to update flow status. Please try again.", {
        autoClose: 1000,
      });
    }
  };

  const header = [
    {
      title: "Name",
      prop: "name",
      isFilterable: true,
    },
    {
      title: "Keywords",
      prop: "keywords",
      cell: (row) => (
        <div className="d-flex flex-wrap gap-1">
          {row.flow?.start?.keywords?.map((keyword, idx) => {
            const s = String(keyword || "");
            let hash = 0;
            for (let i = 0; i < s.length; i++) hash = (hash * 31 + s.charCodeAt(i)) >>> 0;
            const tone = hash % 6;
            return (
              <span key={idx} className={`pill-soft pill-tone-${tone}`}>
                {keyword}
              </span>
            );
          })}
        </div>
      ),
    },
    {
      title: "Status",
      prop: "status",
      cell: (row) => (
        <Button
          type="button"
          variant="light"
          className={
            row.status === "active" ? "pill-soft pill-soft-success" : "pill-soft pill-soft-danger"
          }
          title="Update status"
          onClick={() => handleStatusChange(row)}
        >
          {row.status === "active" ? "Active" : "Inactive"}
        </Button>
      ),
    },
    {
      title: "Actions",
      prop: "actions",
      cell: (row) => (
        <div className="d-flex gap-2">
          <Button
            variant="light"
            size="sm"
            className="btn-action-icon"
            onClick={() => handleEdit(row)}
            title="Edit"
          >
            <FaEdit />
          </Button>
          <Button
            variant="light"
            className="btn-action-icon btn-action-danger"
            onClick={() => handleDelete(row)}
            title="Delete"
          >
            <FaTrash />
          </Button>
        </div>
      ),
    },
  ];

  const reachedChatbotLimit =
    chatbotLimit !== null && flows.length >= chatbotLimit;
  return (
    <>
      <Container className="mt-5">
        <Row className="mx-5 mb-4">
          <Card className="p-4 header-bg shadow-sm border-0 rounded-3">
            <h5 className="mb-2 fw-bold">💡 Quick Guide</h5>
            <small>
              🤖 Chatbot Flow Tip: Design smooth conversation steps that guide
              users naturally. Anticipate questions, provide clear options, and
              keep responses concise to create an engaging and helpful chat
              experience.
            </small>
          </Card>
        </Row>

        <Row className="mx-5 g-0">
          <Col lg={12} className="mb-3">
            <Row className="g-0">
              <Col lg={12}>
                <div className="bg-white shadow-sm border-0 rounded-3 p-4">
                  <h5 className="fw-semibold mb-1">📦 All ChatBot Flow</h5>

                  {isLoading ? (
                    <ShimmerTable row={10} col={4} />
                  ) : (
                    <DatatableWrapper
                      body={flows}
                      headers={header}
                      paginationOptionsProps={{
                        initialState: {
                          rowsPerPage: 10,
                          options: [5, 10, 15, 20],
                        },
                      }}
                    >
                      <Row className="mb-3">
                        <Col
                          lg={3}
                          sm={10}
                          xs={10}
                          className="d-flex justify-content-end align-items-end"
                        >
                          <Filter className="p-2" />
                        </Col>
                        <Col
                          lg={4}
                          sm={2}
                          xs={2}
                          className="d-flex justify-content-start align-items-start"
                        >
                          {/* PaginationOptions can be added if needed */}
                        </Col>
                        <Col
                          lg={5}
                          sm={12}
                          xs={12}
                          className="mt-2 d-flex justify-content-end align-items-end"
                        >
                          {!reachedChatbotLimit && (
                            <Button
                              className="btn btn-sm"
                              variant="outline-secondary"
                              onClick={handleCreate}
                            >
                              <FaPlus /> Add New ChatBot Flow
                            </Button>
                          )}
                        </Col>
                      </Row>

                      <div className="table-responsive">
                        <Table
                          striped
                          bordered
                          className="related-list-table"
                          responsive="sm"
                        >
                          <TableHeader />
                          <TableBody />
                        </Table>
                      </div>
                      <Pagination />
                    </DatatableWrapper>
                  )}

                  {/* {error && (
                    <Alert variant="danger" className="mt-3">
                      {error}
                    </Alert>
                  )} */}
                </div>
              </Col>
            </Row>
          </Col>
        </Row>
      </Container>

      <Confirm
        show={showConfirmModal}
        onHide={() => {
          setShowConfirmModal(false);
          setSelectedFlow(null);
          setDeleting(false);
        }}
        deleteModuleRecord={confirmDelete}
        title="Delete Flow"
        message={`Are you sure you want to delete the flow "${selectedFlow?.name}"? This action cannot be undone and will permanently remove all associated data.`}
        table="deleteFlow"
        loading={deleting}
      />

      <ToastContainer />
    </>
  );
};

export default ChatBotFlowList;
